import { cloneElement, isValidElement, useCallback, useId, useMemo, useState, type ChangeEvent, type FocusEvent, type ReactElement, type ReactNode } from 'react';
import { t } from '../i18n';
import { Tip } from './PageHead';

/**
 * One labelled form control (UX review P2-5): the label with a `*` when the field is required,
 * an optional `i` hint BESIDE the label (never inside it, so the control's accessible name is
 * the label alone), the control, and the field's error UNDER the control.
 *
 * The control is the one child element. `Field` gives it an `id` (unless it has one), wires
 * `aria-invalid`, `aria-required` and `aria-describedby` (the error), and adds the `invalid`
 * class while an error is shown. It does not decide WHEN an error shows — the form does, with
 * `useFieldErrors` below: after the field is left (blur) or after a submit attempt, never
 * while a field nobody has typed in is still empty.
 *
 * Existing queries keep working: `getByLabelText('Name')` finds the control through
 * `<label htmlFor>`; the `*` sits beside the label (not inside it) and is `aria-hidden`.
 */
export interface FieldProps {
  label: ReactNode;
  children: ReactElement;
  required?: boolean;
  /** Help text behind an `i` next to the label. */
  hint?: ReactNode;
  /** The error to SHOW now (already gated by the form); `undefined`/empty shows none. */
  error?: ReactNode;
  /** Spans the whole form grid row (`.field.wide`). */
  wide?: boolean;
  className?: string;
  /** With `form`: the name the form tracks this field by — leaving the control marks it touched. */
  name?: string;
  form?: FieldErrors;
  testid?: string;
}

type ControlProps = {
  id?: string;
  className?: string;
  onBlur?: (e: FocusEvent<HTMLElement>) => void;
  onChange?: (e: ChangeEvent<HTMLElement>) => void;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean | 'true' | 'false';
  'aria-required'?: boolean | 'true' | 'false';
};

export function Field({ label, children, required = false, hint, error, wide = false, className, name, form, testid }: FieldProps) {
  const auto = useId();
  const child = isValidElement<ControlProps>(children) ? children : null;
  const id = child?.props.id ?? `f${auto.replace(/:/g, '')}`;
  const errId = `${id}-err`;
  const shown = error !== undefined && error !== null && error !== false && error !== '';
  const control = child
    ? cloneElement(child, {
        id,
        className: [child.props.className, shown ? 'invalid' : ''].filter(Boolean).join(' ') || undefined,
        'aria-invalid': shown ? true : undefined,
        'aria-required': required ? true : child.props['aria-required'],
        'aria-describedby': [child.props['aria-describedby'], shown ? errId : ''].filter(Boolean).join(' ') || undefined,
        onChange: (e: ChangeEvent<HTMLElement>) => {
          child.props.onChange?.(e);
          if (form && name) form.edit(name);
        },
        onBlur: (e: FocusEvent<HTMLElement>) => {
          child.props.onBlur?.(e);
          if (form && name) form.leave(name);
        },
      })
    : children;
  return (
    <div className={['field', wide ? 'wide' : '', className ?? ''].filter(Boolean).join(' ')} data-testid={testid}>
      <span className="field-head">
        <label htmlFor={id}>{label}</label>
        {/* Beside the label, not in it: the label's text (what `getByLabelText` and a reader
            match) stays exactly the label. `aria-required` on the control says it to a screen reader. */}
        {required && (
          <span className="req" aria-hidden="true" title={t('field.required.tip')}>
            *
          </span>
        )}
        {hint && <Tip>{hint}</Tip>}
      </span>
      {control}
      {shown && (
        <span className="field-error" id={errId}>
          {error}
        </span>
      )}
    </div>
  );
}

/**
 * A group of controls with one label (checkboxes, radios) — a `<fieldset>` whose `<legend>` is
 * the label, with the same `*`, hint and error-under-the-group as `Field`.
 */
export function FieldGroup({
  legend,
  children,
  required = false,
  hint,
  error,
  className,
  testid,
}: {
  legend: ReactNode;
  children: ReactNode;
  required?: boolean;
  hint?: ReactNode;
  error?: ReactNode;
  className?: string;
  testid?: string;
}) {
  const auto = useId();
  const errId = `g${auto.replace(/:/g, '')}-err`;
  const shown = error !== undefined && error !== null && error !== false && error !== '';
  return (
    <fieldset
      className={['checkset', shown ? 'invalid' : '', className ?? ''].filter(Boolean).join(' ')}
      aria-describedby={shown ? errId : undefined}
      data-testid={testid}
    >
      <legend>
        {legend}
        {required && (
          <span className="req" aria-hidden="true" title={t('field.required.tip')}>
            *
          </span>
        )}
        {hint && <Tip>{hint}</Tip>}
      </legend>
      {children}
      {shown && (
        <span className="field-error" id={errId}>
          {error}
        </span>
      )}
    </fieldset>
  );
}

/**
 * When a form shows its field errors: a field's error once the user has CHANGED it and left it
 * (blur), every error after a submit attempt. Passing through a field without typing does not
 * count — a dialog focuses its first field on open, and clicking on into the next one must not
 * paint the first red. `show(name, error)` returns the error to show now (or `undefined`);
 * `attempt(ok)` marks the form submitted and, when it is not ok, moves focus to the first control
 * marked invalid inside `root` (the dialog) once it has rendered. `touch(name)` shows a field's
 * error at once (e.g. a checkbox group whose every tick is a finished answer).
 */
export interface FieldErrors {
  show: <T>(name: string, error: T | undefined | null | false) => T | undefined;
  touch: (name: string) => void;
  /** The control changed (wired by `<Field name form>`). */
  edit: (name: string) => void;
  /** The control lost focus: its error shows from now on if it was changed (wired by `<Field name form>`). */
  leave: (name: string) => void;
  submitted: boolean;
  attempt: (ok: boolean, root?: ParentNode | null) => boolean;
  reset: () => void;
}

export function useFieldErrors(): FieldErrors {
  const [touched, setTouched] = useState<ReadonlySet<string>>(() => new Set());
  const [edited, setEdited] = useState<ReadonlySet<string>>(() => new Set());
  const [submitted, setSubmitted] = useState(false);
  const touch = useCallback((name: string) => setTouched((prev) => (prev.has(name) ? prev : new Set(prev).add(name))), []);
  const edit = useCallback((name: string) => setEdited((prev) => (prev.has(name) ? prev : new Set(prev).add(name))), []);
  const attempt = useCallback((ok: boolean, root?: ParentNode | null) => {
    setSubmitted(true);
    if (!ok) {
      // After React has drawn the errors: the first invalid control takes focus, so its error
      // (its aria-describedby) is what a screen reader reads next.
      setTimeout(() => {
        const scope: ParentNode = root ?? document;
        (scope.querySelector('[aria-invalid="true"], fieldset.invalid input') as HTMLElement | null)?.focus();
      }, 0);
    }
    return ok;
  }, []);
  const reset = useCallback(() => {
    setTouched(new Set());
    setEdited(new Set());
    setSubmitted(false);
  }, []);
  const leave = useCallback((name: string) => {
    if (edited.has(name)) touch(name);
  }, [edited, touch]);
  return useMemo(
    () => ({
      show: <T,>(name: string, error: T | undefined | null | false) =>
        (submitted || touched.has(name)) && error !== null && error !== false && error !== undefined ? error : undefined,
      touch,
      edit,
      leave,
      submitted,
      attempt,
      reset,
    }),
    [submitted, touched, touch, edit, leave, attempt, reset],
  );
}
